import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Edit, 
  Trash2, 
  Plus, 
  Users, 
  Calendar, 
  Clock, 
  UserPlus,
  UserMinus,
  Mail,
  Phone,
  X,
  AlertCircle
} from 'lucide-react';
import Layout from '../components/Layout';
import { useApp } from '../contexts/AppContext';
import { Class, Student, ClassSchedule } from '../types';
import toast from 'react-hot-toast';

const ClassDetailPage: React.FC = () => {
  // Normalize schedule that might arrive as a JSON string from the API
  const normalizeSchedule = (schedule: unknown): ClassSchedule[] => {
    if (Array.isArray(schedule)) return schedule as ClassSchedule[];
    if (typeof schedule === 'string') {
      try {
        const parsed = JSON.parse(schedule || '[]');
        return Array.isArray(parsed) ? parsed as ClassSchedule[] : [];
      } catch {
        return [];
      }
    }
    return [];
  };
  const { classId } = useParams<{ classId: string }>();
  const navigate = useNavigate();
  const { 
    classes, 
    students, 
    updateClass, 
    deleteClass, 
    addStudent, 
    updateStudent, 
    deleteStudent,
    getStudentsInClass,
    addStudentToClass,
    removeStudentFromClass,
    getAvailableStudents,
    loading 
  } = useApp();
  
  const [classData, setClassData] = useState<Class | null>(null);
  const [classStudents, setClassStudents] = useState<Student[]>([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddStudentModal, setShowAddStudentModal] = useState(false);
  const [editingStudent, setEditingStudent] = useState<Student | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    schedule: [] as ClassSchedule[]
  });
  const [studentFormData, setStudentFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    parentName: '',
    parentEmail: '',
    notes: ''
  });
  const [availableStudents, setAvailableStudents] = useState<Student[]>([]);
  const [studentSearchTerm, setStudentSearchTerm] = useState('');

  const daysOfWeek = [
    'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'
  ];

  useEffect(() => {
    const loadClassData = async () => {
      if (classId) {
        const foundClass = classes.find(c => c.id === classId);
        if (foundClass) {
          setClassData(foundClass);
          setFormData({
            name: foundClass.name,
            description: foundClass.description || '',
            schedule: foundClass.schedule || []
          });
          
          try {
            // Load students in this class
            const studentsInClass = await getStudentsInClass(classId);
            setClassStudents(studentsInClass);
            
            // Load available students (not in this class)
            const available = await getAvailableStudents(classId);
            setAvailableStudents(available);
          } catch (error) {
            console.error('Error loading class students:', error);
            toast.error('Failed to load class students');
          }
        } else {
          toast.error('Class not found');
          navigate('/classes');
        }
      }
    };
    
    loadClassData();
  }, [classId, classes, getStudentsInClass, getAvailableStudents, navigate]);

  const handleClassUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Please enter a class name');
      return;
    }

    try {
      if (classData) {
        await updateClass(classData.id, formData);
        toast.success('Class updated successfully');
        setShowEditModal(false);
      }
    } catch (error) {
      toast.error('Failed to update class');
    }
  };

  const handleClassDelete = async () => {
    if (!classData) return;
    
    if (window.confirm(`Are you sure you want to delete "${classData.name}"?`)) {
      try {
        await deleteClass(classData.id);
        toast.success('Class deleted successfully');
        navigate('/classes');
      } catch (error) {
        toast.error('Failed to delete class');
      }
    }
  };

  const handleAddExistingStudent = async (studentId: string) => {
    await handleAddStudentToClass(studentId);
  };

  const handleStudentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!studentFormData.firstName.trim() || !studentFormData.lastName.trim()) {
      toast.error('Please enter first name and last name');
      return;
    }

    try {
      if (editingStudent) {
        await updateStudent(editingStudent.id, studentFormData);
        toast.success('Student updated successfully');
      } else {
        await addStudent(studentFormData);
        toast.success('Student added successfully');
      }
      
      setShowAddStudentModal(false);
      setEditingStudent(null);
      setStudentFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        dateOfBirth: '',
        parentName: '',
        parentEmail: '',
        notes: ''
      });
    } catch (error) {
      toast.error('Failed to save student');
    }
  };

  const handleStudentEdit = (student: Student) => {
    setEditingStudent(student);
    setStudentFormData({
      firstName: student.firstName,
      lastName: student.lastName,
      email: student.email || '',
      phone: student.phone || '',
      dateOfBirth: student.dateOfBirth || '',
      parentName: student.parentName || '',
      parentEmail: student.parentEmail || '',
      notes: student.notes || ''
    });
    setShowAddStudentModal(true);
  };

  const handleStudentDelete = async (studentId: string) => {
    if (window.confirm('Are you sure you want to delete this student?')) {
      try {
        await deleteStudent(studentId);
        toast.success('Student deleted successfully');
      } catch (error) {
        toast.error('Failed to delete student');
      }
    }
  };

  // Class-Student relationship management
  const handleAddStudentToClass = async (studentId: string) => {
    if (!classId) return;
    
    try {
      await addStudentToClass(classId, studentId);
      toast.success('Student added to class successfully');
      
      // Refresh the class students and available students
      const studentsInClass = await getStudentsInClass(classId);
      setClassStudents(studentsInClass);
      
      const available = await getAvailableStudents(classId);
      setAvailableStudents(available);
    } catch (error) {
      console.error('Error adding student to class:', error);
      toast.error('Failed to add student to class');
    }
  };

  const handleRemoveStudentFromClass = async (studentId: string) => {
    if (!classId) return;
    
    if (window.confirm('Are you sure you want to remove this student from the class?')) {
      try {
        await removeStudentFromClass(classId, studentId);
        toast.success('Student removed from class successfully');
        
        // Refresh the class students and available students
        const studentsInClass = await getStudentsInClass(classId);
        setClassStudents(studentsInClass);
        
        const available = await getAvailableStudents(classId);
        setAvailableStudents(available);
      } catch (error) {
        console.error('Error removing student from class:', error);
        toast.error('Failed to remove student from class');
      }
    }
  };

  const addScheduleItem = () => {
    setFormData(prev => ({
      ...prev,
      schedule: [...prev.schedule, { dayOfWeek: 1, startTime: '09:00', endTime: '10:00', isActive: true }]
    }));
  };

  const removeScheduleItem = (index: number) => {
    setFormData(prev => ({
      ...prev,
      schedule: prev.schedule.filter((_, i) => i !== index)
    }));
  };

  const updateScheduleItem = (index: number, field: keyof ClassSchedule, value: any) => {
    setFormData(prev => ({
      ...prev,
      schedule: prev.schedule.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const filteredAvailableStudents = availableStudents.filter(student =>
    `${student.firstName} ${student.lastName}`.toLowerCase().includes(studentSearchTerm.toLowerCase()) ||
    student.email?.toLowerCase().includes(studentSearchTerm.toLowerCase()) ||
    student.parentName?.toLowerCase().includes(studentSearchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <Layout>
        <div className="p-6">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-4"></div>
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-8"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-32 bg-gray-200 dark:bg-gray-700 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!classData) {
    return (
      <Layout>
        <div className="p-6">
          <div className="text-center py-12">
            <AlertCircle className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">Class not found</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              The class you're looking for doesn't exist or has been deleted.
            </p>
            <div className="mt-6">
              <button
                onClick={() => navigate('/classes')}
                className="btn-primary"
              >
                Back to Classes
              </button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/classes')}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <ArrowLeft className="h-5 w-5 text-gray-600 dark:text-gray-400" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{classData.name}</h1>
              <p className="mt-1 text-gray-600 dark:text-gray-300">
                {classData.description || 'No description provided'}
              </p>
            </div>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setShowEditModal(true)}
              className="btn-secondary flex items-center"
            >
              <Edit className="h-4 w-4 mr-2" />
              Edit Class
            </button>
            <button
              onClick={handleClassDelete}
              className="btn-secondary text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 flex items-center"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </button>
          </div>
        </div>

        {/* Class Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="card">
            <div className="flex items-center">
              <Users className="h-8 w-8 text-blue-600 dark:text-blue-400" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Students</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{classStudents.length}</p>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="flex items-center">
              <Calendar className="h-8 w-8 text-green-600 dark:text-green-400" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Schedule Days</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {normalizeSchedule(classData.schedule).filter(s => s.isActive).length}
                </p>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="flex items-center">
              <Clock className="h-8 w-8 text-purple-600 dark:text-purple-400" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Hours/Week</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {normalizeSchedule(classData.schedule)
                    .filter(s => s.isActive)
                    .reduce((total, s) => {
                      const start = new Date(`2000-01-01T${s.startTime}`);
                      const end = new Date(`2000-01-01T${s.endTime}`);
                      const diff = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
                      return total + diff;
                    }, 0)
                    .toFixed(1)
                  }
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Schedule */}
        <div className="card mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Class Schedule</h2>
          </div>
          {normalizeSchedule(classData.schedule).length === 0 ? (
            <p className="text-gray-500 dark:text-gray-400 text-center py-4">No schedule set</p>
          ) : (
            <div className="space-y-2">
              {normalizeSchedule(classData.schedule).map((schedule, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="flex items-center space-x-4">
                    <span className="font-medium text-gray-900 dark:text-white">
                      {daysOfWeek[schedule.dayOfWeek]}
                    </span>
                    <span className="text-gray-600 dark:text-gray-300">
                      {schedule.startTime} - {schedule.endTime}
                    </span>
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      schedule.isActive 
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                        : 'bg-gray-100 text-gray-800 dark:bg-gray-600 dark:text-gray-200'
                    }`}>
                      {schedule.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Students Section */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Students</h2>
            <button
              onClick={() => setShowAddStudentModal(true)}
              className="btn-primary flex items-center"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Student
            </button>
          </div>
          
          {classStudents.length === 0 ? (
            <div className="text-center py-8">
              <Users className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
              <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No students</h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Add students to this class to get started.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {classStudents.map((student) => (
                <div key={student.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900 dark:text-white">{student.firstName} {student.lastName}</h3>
                      {student.email && (
                        <p className="text-sm text-gray-600 dark:text-gray-300 flex items-center mt-1">
                          <Mail className="h-3 w-3 mr-1" />
                          {student.email}
                        </p>
                      )}
                      {student.phone && (
                        <p className="text-sm text-gray-600 dark:text-gray-300 flex items-center mt-1">
                          <Phone className="h-3 w-3 mr-1" />
                          {student.phone}
                        </p>
                      )}
                      {student.parentName && (
                        <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                          Parent: {student.parentName}
                        </p>
                      )}
                    </div>
                    <div className="flex space-x-1">
                      <button
                        onClick={() => handleStudentEdit(student)}
                        className="p-1 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
                        title="Edit student"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleRemoveStudentFromClass(student.id)}
                        className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                        title="Remove from class"
                      >
                        <UserMinus className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Edit Class Modal */}
        {showEditModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                  Edit Class
                </h3>
                <form onSubmit={handleClassUpdate}>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Class Name
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      className="input-field"
                      required
                    />
                  </div>
                  
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Description
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      className="input-field"
                      rows={3}
                    />
                  </div>

                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Schedule
                      </label>
                      <button
                        type="button"
                        onClick={addScheduleItem}
                        className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 flex items-center"
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Add Schedule
                      </button>
                    </div>
                    
                    {formData.schedule.map((schedule, index) => (
                      <div key={index} className="flex items-center space-x-2 mb-2 p-2 border border-gray-200 dark:border-gray-600 rounded">
                        <select
                          value={schedule.dayOfWeek}
                          onChange={(e) => updateScheduleItem(index, 'dayOfWeek', parseInt(e.target.value))}
                          className="flex-1 input-field text-sm"
                        >
                          {daysOfWeek.map((day, dayIndex) => (
                            <option key={dayIndex} value={dayIndex}>{day}</option>
                          ))}
                        </select>
                        <input
                          type="time"
                          value={schedule.startTime}
                          onChange={(e) => updateScheduleItem(index, 'startTime', e.target.value)}
                          className="input-field text-sm"
                        />
                        <input
                          type="time"
                          value={schedule.endTime}
                          onChange={(e) => updateScheduleItem(index, 'endTime', e.target.value)}
                          className="input-field text-sm"
                        />
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={schedule.isActive}
                            onChange={(e) => updateScheduleItem(index, 'isActive', e.target.checked)}
                            className="mr-1"
                          />
                          <span className="text-xs text-gray-600 dark:text-gray-300">Active</span>
                        </label>
                        <button
                          type="button"
                          onClick={() => removeScheduleItem(index)}
                          className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>

                  <div className="flex justify-end space-x-2">
                    <button
                      type="button"
                      onClick={() => setShowEditModal(false)}
                      className="btn-secondary"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn-primary"
                    >
                      Update Class
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Add Student Modal */}
        {showAddStudentModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-10 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
              <div className="mt-3">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    Add Student to Class
                  </h3>
                  <button
                    onClick={() => {
                      setShowAddStudentModal(false);
                      setStudentSearchTerm('');
                    }}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>

                {/* Search */}
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Search students by name, email, or parent name..."
                    value={studentSearchTerm}
                    onChange={(e) => setStudentSearchTerm(e.target.value)}
                    className="input-field w-full"
                  />
                </div>

                {/* Available Students List */}
                <div className="max-h-96 overflow-y-auto">
                  {filteredAvailableStudents.length === 0 ? (
                    <div className="text-center py-8">
                      <Users className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
                      <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
                        {availableStudents.length === 0 ? 'No students available' : 'No students found'}
                      </h3>
                      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        {availableStudents.length === 0 
                          ? 'All students are already assigned to classes.' 
                          : 'Try adjusting your search criteria.'
                        }
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {filteredAvailableStudents.map((student) => (
                        <div key={student.id} className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700">
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900 dark:text-white">{student.firstName} {student.lastName}</h4>
                            {student.email && (
                              <p className="text-sm text-gray-600 dark:text-gray-300 flex items-center mt-1">
                                <Mail className="h-3 w-3 mr-1" />
                                {student.email}
                              </p>
                            )}
                            {student.parentName && (
                              <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                                Parent: {student.parentName}
                              </p>
                            )}
                          </div>
                          <button
                            onClick={() => handleAddExistingStudent(student.id)}
                            className="btn-primary text-sm flex items-center"
                          >
                            <UserPlus className="h-4 w-4 mr-1" />
                            Add to Class
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex justify-end mt-4">
                  <button
                    onClick={() => {
                      setShowAddStudentModal(false);
                      setStudentSearchTerm('');
                    }}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit Student Modal */}
        {editingStudent && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                  Edit Student
                </h3>
                <form onSubmit={handleStudentSubmit}>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Name *
                    </label>
                    <div className="grid grid-cols-2 gap-4">
                      <input
                        type="text"
                        value={studentFormData.firstName}
                        onChange={(e) => setStudentFormData(prev => ({ ...prev, firstName: e.target.value }))}
                        className="input-field"
                        placeholder="First Name"
                        required
                      />
                      <input
                        type="text"
                        value={studentFormData.lastName}
                        onChange={(e) => setStudentFormData(prev => ({ ...prev, lastName: e.target.value }))}
                        className="input-field"
                        placeholder="Last Name"
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      value={studentFormData.email}
                      onChange={(e) => setStudentFormData(prev => ({ ...prev, email: e.target.value }))}
                      className="input-field"
                    />
                  </div>
                  
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Phone
                    </label>
                    <input
                      type="tel"
                      value={studentFormData.phone}
                      onChange={(e) => setStudentFormData(prev => ({ ...prev, phone: e.target.value }))}
                      className="input-field"
                    />
                  </div>
                  
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Date of Birth
                    </label>
                    <input
                      type="date"
                      value={studentFormData.dateOfBirth}
                      onChange={(e) => setStudentFormData(prev => ({ ...prev, dateOfBirth: e.target.value }))}
                      className="input-field"
                    />
                  </div>
                  
                  
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Parent Name
                    </label>
                    <input
                      type="text"
                      value={studentFormData.parentName}
                      onChange={(e) => setStudentFormData(prev => ({ ...prev, parentName: e.target.value }))}
                      className="input-field"
                    />
                  </div>
                  
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Parent Email
                    </label>
                    <input
                      type="email"
                      value={studentFormData.parentEmail}
                      onChange={(e) => setStudentFormData(prev => ({ ...prev, parentEmail: e.target.value }))}
                      className="input-field"
                    />
                  </div>
                  
                  
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Notes
                    </label>
                    <textarea
                      value={studentFormData.notes}
                      onChange={(e) => setStudentFormData(prev => ({ ...prev, notes: e.target.value }))}
                      className="input-field"
                      rows={3}
                    />
                  </div>

                  <div className="flex justify-end space-x-2">
                    <button
                      type="button"
                      onClick={() => {
                        setEditingStudent(null);
                        setStudentFormData({
                          firstName: '',
                          lastName: '',
                          email: '',
                          phone: '',
                          dateOfBirth: '',
                          parentName: '',
                          parentEmail: '',
                          notes: ''
                        });
                      }}
                      className="btn-secondary"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn-primary"
                    >
                      Update Student
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ClassDetailPage;
